import { useEffect, useState } from 'react';
import { cleanImages, imageUrls } from '../lib/assignmentImages';

// ─────────────────────────────────────────────────────────────────────────────
// The teacher's pictures on an assignment, as students see them: a row of
// pictures with captions; tap one to see it big (← → to look through them).
// ─────────────────────────────────────────────────────────────────────────────

export default function AssignmentImages({ images: raw }: { images: unknown }) {
  const images = cleanImages(raw);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [big, setBig] = useState<number | null>(null);
  const key = images.map(i => i.path).join('|');

  useEffect(() => {
    if (!images.length) return;
    imageUrls(images.map(i => i.path)).then(setUrls).catch(() => setUrls({}));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (big == null) return;
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBig(null);
      if (e.key === 'ArrowRight') setBig(b => (b == null ? b : (b + 1) % images.length));
      if (e.key === 'ArrowLeft') setBig(b => (b == null ? b : (b - 1 + images.length) % images.length));
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [big, images.length]);

  if (!images.length) return null;
  const shown = big != null ? images[big] : null;

  return (
    <>
      <div className={`a-images n${Math.min(images.length, 3)}`}>
        {images.map((img, i) => (
          <figure key={img.path}>
            <button type="button" onClick={() => setBig(i)} aria-label={img.caption ? `See bigger: ${img.caption}` : 'See bigger'}>
              {urls[img.path] ? <img src={urls[img.path]} alt={img.caption ?? ''} loading="lazy" style={img.w ? { aspectRatio: `${img.w} / ${img.h}` } : undefined} /> : <div className="img-attach-wait">…</div>}
            </button>
            {img.caption && <figcaption dir="auto">{img.caption}</figcaption>}
          </figure>
        ))}
      </div>
      {shown && (
        <div className="a-lightbox" role="dialog" aria-modal="true" aria-label="Picture" onClick={() => setBig(null)}>
          <figure onClick={e => e.stopPropagation()}>
            {urls[shown.path] && <img src={urls[shown.path]} alt={shown.caption ?? ''} />}
            {shown.caption && <figcaption dir="auto">{shown.caption}</figcaption>}
          </figure>
          <button type="button" className="icon a-lightbox-close" aria-label="Close" onClick={() => setBig(null)}>✕</button>
          {images.length > 1 && (
            <>
              <button type="button" className="a-lightbox-nav prev" aria-label="Previous picture" onClick={e => { e.stopPropagation(); setBig(b => ((b ?? 0) - 1 + images.length) % images.length); }}>‹</button>
              <button type="button" className="a-lightbox-nav next" aria-label="Next picture" onClick={e => { e.stopPropagation(); setBig(b => ((b ?? 0) + 1) % images.length); }}>›</button>
            </>
          )}
        </div>
      )}
    </>
  );
}
